import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

function BlogList() {
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog?page=${page}&limit=9`).then((r) => {
      setPosts(r.data.items);
      setTotalPages(r.data.pages);
    }).catch(console.error).finally(() => setLoading(false));
  }, [page]);

  return (
    <div className="min-h-screen bg-champagne" data-testid="blog-page">
      <Navbar />
      <section className="pt-32 pb-8 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h1 initial="hidden" animate="visible" variants={fadeUp}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-charcoal"
          >
            Blog
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-body text-charcoal-light mt-4 max-w-2xl text-lg"
          >
            Thoughts, reflections, and practical guidance on coaching,
            emotional awareness, parenting, and personal growth.
          </motion.p>
        </div>
      </section>

      <section className="py-12 px-6" data-testid="blog-list">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <p className="font-body text-charcoal-light text-center py-12">Loading...</p>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-heading text-3xl text-charcoal mb-3">Coming Soon</p>
              <p className="font-body text-charcoal-light text-lg max-w-md mx-auto">
                Joyce is preparing thoughtful articles on coaching, emotional awareness, and personal growth.
                Check back soon.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post, i) => (
                  <motion.div
                    key={post.id}
                    initial="hidden" whileInView="visible" viewport={{ once: true }}
                    variants={fadeUp} custom={i * 0.3}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      data-testid={`blog-card-${post.slug}`}
                      className="group block bg-white border border-border rounded-2xl overflow-hidden hover:shadow-lift transition-all duration-300 h-full"
                    >
                      {post.cover_image ? (
                        <div className="h-48 overflow-hidden img-zoom">
                          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="h-48 bg-champagne-dark flex items-center justify-center">
                          <span className="font-heading text-3xl text-navy/20">Blog</span>
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="flex items-center gap-1 font-body text-xs text-charcoal-light">
                            <Calendar className="w-3 h-3" />
                            {new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        </div>
                        <h3 className="font-heading text-xl font-semibold text-charcoal mb-2 group-hover:text-navy transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        {post.excerpt && (
                          <p className="font-body text-sm text-charcoal-light leading-relaxed line-clamp-3">
                            {post.excerpt}
                          </p>
                        )}
                        {post.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {post.tags.map((tag) => (
                              <span key={tag} className="flex items-center gap-1 px-2 py-1 bg-navy/5 text-navy rounded-full font-body text-xs">
                                <Tag className="w-2.5 h-2.5" /> {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex justify-center gap-3 mt-12">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      data-testid={`blog-page-${p}`}
                      className={`w-10 h-10 rounded-full font-body text-sm font-medium transition-all ${
                        page === p ? "bg-navy text-white" : "bg-white border border-border text-charcoal-light hover:border-navy"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`).then((r) => setPost(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-champagne" data-testid="blog-post-page">
        <Navbar />
        <div className="pt-32 text-center"><p className="font-body text-charcoal-light">Loading...</p></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-champagne" data-testid="blog-post-page">
        <Navbar />
        <div className="pt-32 text-center">
          <p className="font-heading text-2xl text-charcoal mb-4">Post not found</p>
          <Link to="/blog" className="text-navy font-body font-semibold">Back to Blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-champagne" data-testid="blog-post-page">
      <Navbar />
      <article className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 font-body text-sm text-charcoal-light hover:text-navy transition-colors mb-8" data-testid="blog-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>

          {post.cover_image && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl overflow-hidden mb-8 shadow-soft">
              <img src={post.cover_image} alt={post.title} className="w-full h-64 md:h-96 object-cover" />
            </motion.div>
          )}

          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="flex items-center gap-3 mb-4">
              <span className="flex items-center gap-1 font-body text-sm text-charcoal-light">
                <Calendar className="w-4 h-4" />
                {new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-charcoal mb-6 leading-tight">
              {post.title}
            </h1>
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {post.tags.map((tag) => (
                  <span key={tag} className="flex items-center gap-1 px-3 py-1.5 bg-navy/5 text-navy rounded-full font-body text-xs">
                    <Tag className="w-3 h-3" /> {tag}
                  </span>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="prose prose-lg max-w-none font-body text-charcoal-light leading-relaxed"
            data-testid="blog-post-content"
            dangerouslySetInnerHTML={post.content.includes("<") ? { __html: post.content.replace(/&nbsp;/g, " ").replace(/\u00a0/g, " ") } : undefined}
          >
            {!post.content.includes("<") ? post.content.replace(/\u00a0/g, " ").split("\n").map((para, i) => (
              para.trim() ? <p key={i} className="mb-4">{para}</p> : null
            )) : null}
          </motion.div>
        </div>
      </article>
      <Footer />
    </div>
  );
}

export default function BlogPage() {
  const { slug } = useParams();
  return slug ? <BlogPost /> : <BlogList />;
}
